import apiClient from './client';
import { User } from '@/types';

// Pagination types
export interface PaginationParams {
  page?: number;
  limit?: number;
  search?: string;
  active?: boolean;
  roles?: string[];
  condominiumId?: string;
  establishmentId?: string;
}

export interface PaginatedResponse<T> {
  data: T[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

// User creation/update DTOs
export interface CreateUserDto {
  name: string;
  email: string;
  password: string;
  roles: string[];
  phone?: string;
  establishmentId?: string;
  /** Texto livre legado. Preferir `residentialUnitId`. */
  unit?: string;
  /**
   * Unidade da tabela `residential_units`. É o que preenche a coluna
   * "Unidade" das listagens — o texto livre só sobrevive em cadastros
   * anteriores à tabela.
   */
  residentialUnitId?: string;
  cpf?: string;
  companyCnpj?: string;
  companyName?: string;
}

export interface UpdateUserDto {
  name?: string;
  email?: string;
  password?: string;
  roles?: string[];
  phone?: string;
  condominiumId?: string;
  /** Texto livre legado. Preferir `residentialUnitId`. */
  unit?: string;
  residentialUnitId?: string;
  active?: boolean;
  cpf?: string;
  companyCnpj?: string;
  companyName?: string;
}

// User API service
const usersService = {
  // List users with pagination and filters
  async list(params: PaginationParams = {}): Promise<PaginatedResponse<User>> {
    const trimmedSearch = params.search?.trim();
    const { data } = await apiClient.get('/users', {
      params: {
        page: params.page || 1,
        limit: params.limit || 10,
        search: trimmedSearch ? trimmedSearch : undefined,
        active: params.active,
        roles: params.roles?.join(','),
        establishmentId: params.establishmentId || params.condominiumId,
      },
    });
    interface ApiPayload {
      data?: User[];
      meta?: {
        totalItems?: number;
        currentPage?: number;
        itemsPerPage?: number;
        totalPages?: number;
      };
      total?: number;
      page?: number;
      limit?: number;
      totalPages?: number;
    }
    const payload: ApiPayload = (data || {}) as ApiPayload;
    const rows: User[] = payload.data || [];
    const meta = payload.meta || {};

    const total = meta.totalItems ?? payload.total ?? rows.length ?? 0;
    const page = meta.currentPage ?? payload.page ?? params.page ?? 1;
    const limit = meta.itemsPerPage ?? payload.limit ?? params.limit ?? 10;
    const totalPages = meta.totalPages
      ?? payload.totalPages
      ?? (limit ? Math.ceil(total / limit) : 0);

    return {
      data: rows,
      total,
      page,
      limit,
      totalPages,
    };
  },

  // Get a single user by ID
  async getById(id: string): Promise<User> {
    const { data } = await apiClient.get<User>(`/users/${id}`);
    return data;
  },

  // Create a new user
  async create(userData: CreateUserDto): Promise<User> {
    const { data } = await apiClient.post<User>('/users', userData);
    return data;
  },

  // Update an existing user
  async update(id: string, userData: UpdateUserDto): Promise<User> {
    const { data } = await apiClient.patch<User>(`/users/${id}`, userData);
    return data;
  },

  // Delete a user (soft delete)
  async delete(id: string): Promise<void> {
    await apiClient.delete(`/users/${id}`);
  },

  // Toggle user active status
  async toggleStatus(id: string, isActive: boolean): Promise<User> {
    const { data } = await apiClient.patch<User>(`/users/${id}/status`, { isActive });
    return data;
  },

  // Check if email is available
  async checkEmailAvailability(email: string, userId?: string): Promise<boolean> {
    const { data } = await apiClient.get<{ available: boolean }>('/users/check-email', {
      params: { email, userId },
    });
    
    return data.available;
  },

  // Get establishments for current user
  async getUserEstablishments(): Promise<UserEstablishment[]> {
    const { data } = await apiClient.get<UserEstablishment[]>('/users/me/establishments');
    return data;
  },

  // Import residents from Excel file
  async importUsers(file: File): Promise<UserImportResult> {
    const formData = new FormData();
    formData.append('file', file);
    const { data } = await apiClient.post<UserImportResult>(
      '/users/import',
      formData,
      {
        headers: { 'Content-Type': 'multipart/form-data' },
      },
    );
    return data;
  },

  // Download Excel template for resident import
  async getImportTemplate(): Promise<Blob> {
    const { data } = await apiClient.get<Blob>(
      '/users/import/template',
      { responseType: 'blob' },
    );
    return data;
  },
};

export interface UserImportResult {
  imported: number;
  failed: number;
  associated: number;
  errors: Array<{ row: number; name: string; error: string }>;
}

// Types for user establishments
export interface UserEstablishment {
  id: string;
  name: string;
  document?: string;
  cnpj?: string;
  roles: string[];
  permissions: string[];
  /**
   * Toggle controlado pelo síndico que indica se a categoria/tela
   * "Governança" deve aparecer para o morador no app mobile.
   */
  showGovernanceToResidents?: boolean;
}

export default usersService;
